import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowUpRight, CalendarClock, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

import {
  fetchSmeUpcomingIpos,
  fetchUpcomingIpos,
  selectOpeningSoonIpos,
  selectSmeOpeningSoonIpos,
} from '../../store/slices/upcomingIposSlice';
import { OPENING_STATUS, filterOpeningSoon, formatCr, formatInr, openingLabel } from '../../utils/openingSoonIpos';
import { formatWeekdayShort } from '../../utils/ipoCalendar';
import { buildPageList } from '../../utils/gmpBoard';
import { selectCompanyLogos } from '../../store/selectors/companyLogos';
import CompanyLogo from '../common/CompanyLogo';

// Which API the section reads: 'all' = /api/market/ipos/upcoming, 'sme' = /api/market/ipos/sme/upcoming
const FEEDS = {
  all: { fetch: fetchUpcomingIpos, selectRows: selectOpeningSoonIpos, stateKey: 'upcomingIpos' },
  sme: { fetch: fetchSmeUpcomingIpos, selectRows: selectSmeOpeningSoonIpos, stateKey: 'smeUpcomingIpos' },
};

const PAGE_SIZE = 10;
const COLUMN_COUNT = 9;

const BOARD_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'mainboard', label: 'Mainboard' },
  { key: 'sme', label: 'SME' },
];

const formatRefreshedAt = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const Empty = () => <span className="text-slate-300">—</span>;

const SkeletonRows = () =>
  Array.from({ length: 4 }).map((_, idx) => (
    <tr key={`opening-skel-${idx}`} className="border-t border-slate-100 animate-pulse">
      <td className="py-3 pl-4 sm:pl-5 pr-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-200" />
          <div className="space-y-1.5">
            <div className="w-40 h-4 bg-slate-200 rounded" />
            <div className="w-24 h-3 bg-slate-100 rounded" />
          </div>
        </div>
      </td>
      {Array.from({ length: COLUMN_COUNT - 1 }).map((__, col) => (
        <td key={col} className="py-3 px-3">
          <div className="w-16 h-4 bg-slate-100 rounded" />
        </td>
      ))}
    </tr>
  ));

const selectClass =
  'px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]';

// lockedBoard ('sme' | 'mainboard') fixes the list to one board and hides the board tabs;
// feed picks the API the rows come from (see FEEDS)
export const OpeningSoonSection = ({
  today,
  lockedBoard = null,
  feed = 'all',
  title = 'IPOs Opening Soon',
  subtitle = 'Issues scheduled to open for bidding on NSE, with price band, lot size and issue size.',
}) => {
  const source = FEEDS[feed] || FEEDS.all;
  const dispatch = useDispatch();
  const rows = useSelector(source.selectRows);
  const { refreshedAt, status, error } = useSelector((state) => state[source.stateKey]);
  // Logos only exist on extracted filings, never on the NSE schedule feed this
  // table reads, so a row shows one when some other page already loaded it
  const logos = useSelector(selectCompanyLogos);
  const [board, setBoard] = useState(lockedBoard || 'all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);

  // Keep showing loaded rows while a refresh is in flight or after it fails
  const hasRows = rows.length > 0;
  const isLoading = !hasRows && (status === 'idle' || status === 'loading');
  const isFailed = !hasRows && status === 'failed';

  useEffect(() => {
    dispatch(source.fetch({ maxAgeMs: 60000 }));
  }, [dispatch, source]);

  useEffect(() => {
    setPage(1);
  }, [board, statusFilter]);

  // Board counts respect the status filter and vice versa, so each number
  // matches what clicking it would show
  const boardCounts = useMemo(() => {
    const byStatus = filterOpeningSoon(rows, { status: statusFilter });
    return Object.fromEntries(BOARD_FILTERS.map((option) => [option.key, filterOpeningSoon(byStatus, { board: option.key }).length]));
  }, [rows, statusFilter]);
  const statusCounts = useMemo(() => {
    const byBoard = filterOpeningSoon(rows, { board });
    return Object.fromEntries(Object.keys(OPENING_STATUS).map((key) => [key, filterOpeningSoon(byBoard, { status: key }).length]));
  }, [rows, board]);

  const filteredRows = useMemo(() => filterOpeningSoon(rows, { board, status: statusFilter }), [rows, board, statusFilter]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const firstIndex = filteredRows.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const lastIndex = Math.min(currentPage * PAGE_SIZE, filteredRows.length);
  const filtersActive = board !== (lockedBoard || 'all') || statusFilter !== 'all';
  const boardNoun = lockedBoard === 'sme' ? 'SME IPOs' : 'IPOs';
  // Something exists that a reset would bring back (a locked board never resets)
  const canReset = filterOpeningSoon(rows, { board: lockedBoard || 'all' }).length > 0;

  const resetFilters = () => {
    setBoard(lockedBoard || 'all');
    setStatusFilter('all');
  };
  const handleRefresh = () => dispatch(source.fetch());
  const refreshedLabel = formatRefreshedAt(refreshedAt);

  let body;
  if (isLoading) {
    body = <SkeletonRows />;
  } else if (isFailed) {
    body = (
      <tr>
        <td colSpan={COLUMN_COUNT} className="py-12 text-center">
          <div className="max-w-md mx-auto space-y-3 px-4">
            <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
              <RefreshCw className="w-5 h-5" />
            </div>
            <p className="text-slate-700 font-semibold text-sm">Upcoming IPO schedule is temporarily unavailable</p>
            <p className="text-slate-400 text-xs">{error}</p>
            <button
              type="button"
              onClick={handleRefresh}
              className="px-4 py-1.5 bg-[#008080] text-white text-xs font-semibold rounded-lg hover:bg-[#006666] transition-colors"
            >
              Retry Loading
            </button>
          </div>
        </td>
      </tr>
    );
  } else if (pageRows.length === 0) {
    body = (
      <tr>
        <td colSpan={COLUMN_COUNT} className="py-12 text-center">
          <CalendarClock className="w-8 h-8 mx-auto text-slate-300" />
          <p className="mt-2 font-semibold text-slate-700 text-sm">
            {canReset ? `No ${boardNoun} match these filters.` : `No ${boardNoun} are scheduled to open right now.`}
          </p>
          {canReset ? (
            <button type="button" onClick={resetFilters} className="mt-1 text-xs font-bold text-[#008080] hover:underline">
              Reset filters
            </button>
          ) : (
            <p className="mt-1 text-xs text-slate-400">New issues appear here as soon as NSE announces their bidding dates.</p>
          )}
        </td>
      </tr>
    );
  } else {
    body = pageRows.map((row) => (
      <tr key={row.id} className="group border-t border-slate-100 bg-white hover:bg-slate-50/80 transition-colors">
        {/* Company & Board */}
        <td className="sticky left-0 z-10 py-3 pl-4 sm:pl-5 pr-3 bg-white group-hover:bg-slate-50">
          <div className="flex items-center gap-3">
            <CompanyLogo company={row.company} logoUrl={logos.lookup(row.company)} size="sm" />
            <div className="min-w-0">
              <div className="font-bold text-slate-900 leading-snug">{row.company}</div>
              <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-400">
                {row.symbol && <span className="font-semibold">{row.symbol}</span>}
                <span
                  className={`px-1.5 py-px rounded border text-[9px] font-bold uppercase ${
                    row.board === 'sme' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-teal-50 text-[#008080] border-teal-200'
                  }`}
                >
                  {row.boardLabel}
                </span>
              </div>
            </div>
          </div>
        </td>

        {/* Status */}
        <td className="py-3 px-3 whitespace-nowrap">
          <span className={`inline-flex px-2 py-1 rounded-lg border text-[11px] font-bold ${OPENING_STATUS[row.phase].badge}`}>
            {OPENING_STATUS[row.phase].label}
          </span>
          <div className="mt-1 text-[11px] text-slate-500">{openingLabel(row, today)}</div>
        </td>

        <td className="py-3 px-3 whitespace-nowrap font-semibold text-slate-800">
          {row.openOn ? formatWeekdayShort(row.openOn) : <Empty />}
        </td>
        <td className="py-3 px-3 whitespace-nowrap text-slate-600">{row.closeOn ? formatWeekdayShort(row.closeOn) : <Empty />}</td>
        <td className="py-3 px-3 whitespace-nowrap font-semibold text-slate-800">{row.priceBand || <Empty />}</td>
        <td className="py-3 px-3 whitespace-nowrap text-slate-600">
          {row.lotSize ? `${row.lotSize.toLocaleString('en-IN')} shares` : <Empty />}
        </td>
        <td className="py-3 px-3 whitespace-nowrap font-semibold text-slate-800">{formatInr(row.minInvestment) || <Empty />}</td>

        {/* Issue Size */}
        <td className="py-3 px-3 whitespace-nowrap">
          {row.sizeCr ? (
            <>
              <div className="font-semibold text-slate-800">{formatCr(row.sizeCr)}</div>
              <div className="text-[11px] text-slate-400">{row.shares.toLocaleString('en-IN')} shares</div>
            </>
          ) : (
            <Empty />
          )}
        </td>

        <td className="py-3 pl-3 pr-4 sm:pr-5 text-right">
          <Link
            to={`/ipo-gmp/${row.gmpSlug}`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-[11px] font-bold text-slate-600 hover:border-teal-200 hover:text-[#008080] whitespace-nowrap"
          >
            View GMP
            <ArrowUpRight className="w-3 h-3" />
          </Link>
        </td>
      </tr>
    ));
  }

  return (
    <section aria-labelledby="opening-soon-heading">
      <div className="mb-4">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-teal-200 bg-teal-50 text-[11px] font-bold text-[#008080]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#008080] animate-pulse" />
          Bidding Dates Confirmed
        </span>
        <h2 id="opening-soon-heading" className="mt-2 text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">
          {title}
        </h2>
        <p className="mt-1 text-xs sm:text-sm text-slate-500">
          {subtitle}
          {refreshedLabel && <span className="text-slate-400"> Updated {refreshedLabel}.</span>}
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
        {/* Board tabs + Status filter */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-slate-100">
          {lockedBoard ? (
            <span className="self-start px-3 py-1.5 rounded-xl border border-teal-200 bg-teal-50 text-xs font-bold text-[#008080]">
              {boardNoun} ({boardCounts[lockedBoard] || 0})
            </span>
          ) : (
          <div role="group" aria-label="Board" className="flex gap-1 p-1 rounded-xl bg-slate-100 border border-slate-200/80 self-start overflow-x-auto">
            {BOARD_FILTERS.map((option) => (
              <button
                key={option.key}
                type="button"
                aria-pressed={board === option.key}
                onClick={() => setBoard(option.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                  board === option.key ? 'bg-white text-[#008080] shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {option.label === 'All' ? 'All IPOs' : `${option.label} IPOs`} ({boardCounts[option.key] || 0})
              </button>
            ))}
          </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Status</span>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectClass}>
                <option value="all">All statuses</option>
                {Object.entries(OPENING_STATUS).map(([key, meta]) => (
                  <option key={key} value={key}>
                    {meta.label} ({statusCounts[key] || 0})
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={resetFilters}
              disabled={!filtersActive}
              className="text-xs font-bold text-[#008080] hover:underline disabled:text-slate-300 disabled:no-underline disabled:cursor-default px-1"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={status === 'loading'}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:text-[#008080] hover:border-teal-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex-shrink-0"
              aria-label="Refresh upcoming IPOs"
              title="Refresh upcoming IPOs"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${status === 'loading' ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto scrollbar-slim">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/90 border-b border-slate-200/80 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                <th className="sticky left-0 z-10 bg-slate-50 py-3 pl-4 sm:pl-5 pr-3 min-w-[240px]">Company &amp; Board</th>
                <th className="py-3 px-3 whitespace-nowrap">Status</th>
                <th className="py-3 px-3 whitespace-nowrap">Opens</th>
                <th className="py-3 px-3 whitespace-nowrap">Closes</th>
                <th className="py-3 px-3 whitespace-nowrap">Price Band</th>
                <th className="py-3 px-3 whitespace-nowrap">Lot Size</th>
                <th className="py-3 px-3 whitespace-nowrap">Min. Investment</th>
                <th className="py-3 px-3 whitespace-nowrap">Issue Size</th>
                <th className="py-3 pl-3 pr-4 sm:pr-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="text-xs sm:text-[13px]">{body}</tbody>
          </table>
        </div>

        {/* Footer */}
        {!isLoading && !isFailed && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-t border-slate-200/80 bg-slate-50/70">
            <p className="text-xs text-slate-500">
              Showing {firstIndex} to {lastIndex} of {filteredRows.length} {filteredRows.length === 1 ? boardNoun.replace(/s$/, '') : boardNoun}
              {filteredRows.some((row) => !row.lotSize) && (
                <span className="text-slate-400"> · Lot size and minimum investment show once NSE publishes them.</span>
              )}
            </p>
            {totalPages > 1 && (
              <nav className="flex items-center gap-1" aria-label="Upcoming IPOs pagination">
                <button
                  type="button"
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  Prev
                </button>
                {buildPageList(currentPage, totalPages).map((p, i) =>
                  p === '…' ? (
                    <span key={`gap-${i}`} className="w-7 text-center text-xs text-slate-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(p)}
                      aria-current={p === currentPage ? 'page' : undefined}
                      className={`w-8 h-8 rounded-lg text-xs font-bold ${
                        p === currentPage ? 'bg-[#008080] text-white' : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  type="button"
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </nav>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default OpeningSoonSection;
